import React, { useState } from 'react';
import { Target, Flag, Clock, CheckCircle, Plus, Folder, Trash2, Settings, Key } from "lucide-react";

export default function RoadmapTab({ ctx }) {
  const { T, isEN, projects, roadmapTasks, versions, setVersions, api, notify, isAdmin, nl2Settings, currentUser, teams, loadAllTasks, setRoadmapTasks, allTasks } = ctx; 
  
  const [newVersionName, setNewVersionName] = useState("");
  const [newVersionTarget, setNewVersionTarget] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState({});
  const [newTaskProject, setNewTaskProject] = useState({});

  const workingTeamId = nl2Settings?.working_team_id || null;
  const canWork = isAdmin || !workingTeamId || (teams.find(tm => tm.id === parseInt(workingTeamId))?.memberIds || []).includes(currentUser?.id);

  // Hilfsfunktion für Hauptaufgaben (da diese aus der DB geladen werden)
  const getTaskStats = (taskData) => {
    const liveTask = allTasks?.find(t => String(t.id) === String(taskData.id));
    if (liveTask && Array.isArray(liveTask.checklist)) {
      return {
        total: liveTask.checklist.length,
        done: liveTask.checklist.filter(c => c.done).length
      };
    }
    return {
      total: parseInt(taskData.checklist_total) || 0,
      done: parseInt(taskData.checklist_done) || 0
    };
  };

  const handleCreateVersion = async () => {
    if (!newVersionName.trim()) return;
    try {
      const v = await api('/versions', { method: "POST", body: JSON.stringify({ name: newVersionName, release_target: newVersionTarget || null }) });
      setVersions([...versions, v]);
      setNewVersionName("");
      setNewVersionTarget("");
      notify("Version erstellt ✅");
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const handleQuickTask = async (versionId) => {
    if (!canWork) return; 
    const title = newTaskTitle[versionId];
    const projectId = newTaskProject[versionId];
    if (!title || !projectId) { notify("Titel und Projekt erforderlich!"); return; }
    
    try {
      const newTask = await api(`/projects/${projectId}/tasks`, { 
        method: "POST", 
        body: JSON.stringify({ title, status: "Offen", priority: 2, assignees: [], checklist: [], tagIds: [], depends_on: [] }) 
      });
      await api(`/tasks/${newTask.id}/version`, { method: "PUT", body: JSON.stringify({ version_id: versionId }) });
      
      notify("Aufgabe zur Version hinzugefügt ✅");
      setNewTaskTitle({ ...newTaskTitle, [versionId]: "" });
      
      if (loadAllTasks) loadAllTasks();
      const updatedTasks = await api('/roadmap/tasks');
      if (setRoadmapTasks) setRoadmapTasks(updatedTasks);
      
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const handleDeleteVersion = async (id) => {
    if(!window.confirm("Version löschen? Die Aufgaben in den Projekten bleiben erhalten.")) return;
    try {
      await api(`/versions/${id}`, { method: "DELETE" });
      setVersions(versions.filter(v => v.id !== id));
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const toggleLockVersion = async (version) => {
    try {
      const newLockState = !version.is_locked;
      await api(`/versions/${version.id}`, { 
        method: "PUT", 
        body: JSON.stringify({ ...version, is_locked: newLockState }) 
      });
      setVersions(versions.map(v => v.id === version.id ? { ...v, is_locked: newLockState } : v));
      notify(newLockState ? "Version gesperrt! 🔒" : "Version entsperrt! 🔓");
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const handleEditVersion = async (version) => {
    const newName = window.prompt(isEN ? "New version name:" : "Neuer Name für die Version:", version.name);
    if (newName === null) return; 
    
    const newTarget = window.prompt(isEN ? "Release Target (e.g. Q3 2026 / Winter):" : "Release-Ziel / Schätzung (z.B. 'Q3 2026', 'Sommer' oder ein Datum):", version.release_target || "");
    if (newTarget === null) return;

    try {
      await api(`/versions/${version.id}`, { 
        method: "PUT", 
        body: JSON.stringify({ 
          name: newName.trim() || version.name, 
          release_target: newTarget.trim() 
        }) 
      });
      
      const updatedVersions = await api('/versions');
      setVersions(updatedVersions);
      notify("Version aktualisiert ✅");
    } catch(e) { notify("Fehler: " + e.message); }
  };
  
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* HEADER & CREATE VERSION */}
      <div style={{ background: T.card, padding: "24px", borderRadius: 16, border: `1px solid ${T.border}`, display: "flex", flexWrap: "wrap", gap: 20, justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
            <Target color="#8B5CF6" size={28} /> {isEN ? "Global Roadmap" : "Globale Roadmap"}
          </h2>
          <p style={{ color: T.textMuted, fontSize: 14 }}>{isEN ? "Manage cross-project versions and feature releases." : "Projektübergreifende Versionen und Feature-Releases."}</p>
        </div>
        
        {isAdmin && (
          <div style={{ display: "flex", gap: 10, background: T.bg, padding: 8, borderRadius: 12, border: `1px solid ${T.border2}`, flexWrap: "wrap" }}>
            <input value={newVersionName} onChange={e=>setNewVersionName(e.target.value)} placeholder="v2.12 Alpha..." style={{ background: "transparent", border: "none", color: T.text, outline: "none", padding: "0 8px", width: 140 }} />
            <input value={newVersionTarget} onChange={e=>setNewVersionTarget(e.target.value)} placeholder="Z.B. Q3 2026" style={{ background: T.cardHover, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, padding: "8px", outline: "none", fontSize: 12, width: 120 }} />
            <button onClick={handleCreateVersion} style={{ background: "#8B5CF6", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer" }}>+ {isEN?"Version":"Version"}</button>
          </div>
        )}
      </div>

      {/* VERSIONS LIST */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {versions.map(version => {
          const versionTasks = (roadmapTasks || []).filter(t => t.version_id === version.id);
          
          const completedCount = versionTasks.filter(t => {
            const s = (t.status || "").toLowerCase();
            return s === "erledigt" || s.includes("fix") || s.includes("check");
          }).length;
          
          const progress = versionTasks.length > 0 ? Math.round((completedCount / versionTasks.length) * 100) : 0;          
          const releaseDisplay = version.release_target || (version.release_date ? new Date(version.release_date).toLocaleDateString() : null);

          return (
            <div key={version.id} style={{ background: T.card, borderRadius: 16, border: `1px solid ${T.border}`, overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
              {/* VERSION HEADER */}
              <div style={{ padding: "16px 24px", background: "rgba(139, 92, 246, 0.05)", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "#8B5CF620", color: "#8B5CF6", display: "flex", alignItems: "center", justifyContent: "center" }}><Flag size={20}/></div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                      {version.name}
                      <span style={{ fontSize: 12, background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "2px 8px", borderRadius: 12 }}>{progress}%</span>
                    </div>
                    {releaseDisplay && <div style={{ fontSize: 12, color: T.textFaint, display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}><Clock size={12}/> Release: {releaseDisplay}</div>}
                  </div>
                </div>
                
                {isAdmin && (
                  <div style={{display: "flex", gap: 8}}>
                    <button onClick={() => toggleLockVersion(version)} style={{ background: "transparent", border: "none", color: version.is_locked ? "#F59E0B" : T.textMuted, cursor: "pointer", padding: 8 }} title={version.is_locked ? "Version entsperren" : "Version sperren"}>
                      {version.is_locked ? <Key size={16}/> : <Settings size={16}/>}
                    </button>
                    <button onClick={() => handleEditVersion(version)} style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer", padding: 8 }} className="hov" title="Version bearbeiten">
                      <Settings size={16}/>
                    </button>
                    <button onClick={() => handleDeleteVersion(version.id)} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: 8, opacity: 0.5 }} className="hov" title="Version löschen">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                )}
              </div>

              {/* PROGRESS BAR */}
              <div style={{ height: 4, background: T.border }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "#8B5CF6", transition: "width 0.5s ease" }} />
              </div>

              {/* TASKS IN VERSION */}
              <div style={{ padding: 20 }}>
                {versionTasks.length === 0 ? (
                  <div style={{ textAlign: "center", color: T.textFaint, padding: "20px 0", fontSize: 13 }}>{isEN?"No tasks assigned yet.":"Noch keine Aufgaben zugewiesen."}</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    {(() => {
                      // 1. Filter für echte Hauptaufgaben (kein parent_task_id, oder parent_task_id nicht in dieser Roadmap)
                      const topLevelTasks = versionTasks.filter(t => !t.parent_task_id || !versionTasks.some(p => p.id === t.parent_task_id));

                      return topLevelTasks.map(task => {
                        const s = (task.status || "").toLowerCase();
                        const isDone = s === "erledigt" || s.includes("fix") || s.includes("check");
                        
                        const stats = getTaskStats(task);
                        const cTotal = stats.total;
                        const cDone = stats.done;

                        // 🚀 DIE MAGIE: Wir ziehen die Unteraufgaben aus dem LIVE-SPEICHER (allTasks)
                        // Dadurch erscheinen auch Unteraufgaben, die im Detail-Panel erstellt wurden und keine offizielle 'version_id' haben.
                        const subTasks = (allTasks || []).filter(t => String(t.parent_task_id) === String(task.id) && !t.archived);

                        return (
                          <div key={task.id} style={{ display: "flex", flexDirection: "column", background: T.bg, border: `1px solid ${T.border2}`, borderRadius: 10, overflow: "hidden" }}>
                            
                            {/* --- DIE HAUPTAUFGABE --- */}
                            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", opacity: isDone ? 0.6 : 1, borderBottom: subTasks.length > 0 ? `1px solid ${T.border2}` : "none" }}>
                              <CheckCircle size={16} color={isDone ? "#34D399" : T.textGhost} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: isDone ? T.textMuted : T.text, textDecoration: isDone ? "line-through" : "none" }}>{task.title}</div>
                                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Folder size={10}/> {task.project_name || "Unbekanntes Projekt"}</span>
                                  {cTotal > 0 && (
                                    <span style={{ fontSize: 10, color: cDone === cTotal ? "#34D399" : T.textFaint, background: T.cardHover, padding: "2px 6px", borderRadius: 5, fontWeight: 600 }}>
                                      ✓ {cDone}/{cTotal}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{ fontSize: 11, background: T.cardHover, color: T.textMuted, padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}` }}>{task.status}</span>
                            </div>

                            {/* --- DIE EINGERÜCKTEN UNTERAUFGABEN --- */}
                            {subTasks.length > 0 && (
                              <div style={{ display: "flex", flexDirection: "column", background: T.cardHover }}>
                                {subTasks.map((sub, idx) => {
                                  const sSub = (sub.status || "").toLowerCase();
                                  const isSubDone = sSub === "erledigt" || sSub.includes("fix") || sSub.includes("check");
                                  
                                  // Da 'sub' direkt aus allTasks kommt, können wir die Checkliste direkt auslesen!
                                  const subCTotal = (sub.checklist || []).length;
                                  const subCDone = (sub.checklist || []).filter(c => c.done).length;

                                  return (
                                    <div key={sub.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px 8px 36px", opacity: isSubDone ? 0.6 : 1, borderBottom: idx < subTasks.length - 1 ? `1px dashed ${T.border2}` : "none" }}>
                                      <div style={{ color: T.textGhost, fontSize: 14, fontWeight: 700, transform: "translateY(-2px)" }}>↳</div>
                                      
                                      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: isSubDone ? T.textMuted : T.text, textDecoration: isSubDone ? "line-through" : "none" }}>{sub.title}</div>
                                        {/* 🚀 Hier werden die Checklisten-Badges für Unteraufgaben generiert */}
                                        {subCTotal > 0 && (
                                          <span style={{ fontSize: 10, color: subCDone === subCTotal ? "#34D399" : T.textFaint, background: T.bg, border: `1px solid ${T.border2}`, padding: "2px 6px", borderRadius: 5, fontWeight: 600 }}>
                                            ✓ {subCDone}/{subCTotal}
                                          </span>
                                        )}
                                      </div>
                                      
                                      <span style={{ fontSize: 11, background: T.bg, color: T.textMuted, padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.border}` }}>{sub.status}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* QUICK ADD TASK INLINE */}
                {canWork && (
                  <div style={{ display: "flex", gap: 10, alignItems: "center", background: T.bg, padding: 8, borderRadius: 10, border: `1px dashed ${T.border3}`, flexWrap: "wrap" }}>
                    <Plus size={16} color={T.textMuted} />
                    <input value={newTaskTitle[version.id] || ""} onChange={e => setNewTaskTitle({...newTaskTitle, [version.id]: e.target.value})} placeholder={isEN?"Task title...":"Aufgabentitel..."} style={{ background: "transparent", border: "none", color: T.text, flex: 1, minWidth: 150, outline: "none", fontSize: 13 }} />
                    <select value={newTaskProject[version.id] || ""} onChange={e => setNewTaskProject({...newTaskProject, [version.id]: e.target.value})} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.text, borderRadius: 8, padding: "6px 10px", outline: "none", fontSize: 12 }}>
                      <option value="">{isEN?"Select Project":"Projekt wählen"}</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button onClick={() => handleQuickTask(version.id)} style={{ background: T.card, border: `1px solid ${T.border2}`, color: T.text, borderRadius: 8, padding: "6px 12px", fontWeight: 600, cursor: "pointer", fontSize: 12 }} className="hov">{isEN?"Add":"Hinzufügen"}</button>
                  </div>
                )}
                
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}